class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', (event) => {
      if (event.target.id === 'CartDrawer-Overlay') this.close();
    });
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  lockScroll() {
    if (document.body.classList.contains('cart-drawer-overflow-hidden')) return
    const scrollY = window.scrollY;
    document.body.style.top = `-${scrollY}px`;
    document.body.classList.add('cart-drawer-overflow-hidden');
    document.body.classList.add('is-open-cart-drawer');
  }

  unlockScroll() {
    const scrollY = document.body.style.top;
    document.body.classList.remove('cart-drawer-overflow-hidden');
    document.body.classList.remove('is-open-cart-drawer');
    document.body.style.top = '';
    window.scrollTo({
      top: parseInt(scrollY || '0') * -1,
      behavior: 'instant'
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    this.lockScroll();
    const contents = this.querySelector('cart-drawer-items');
    if (contents) contents.scrollTop = 0;

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = (this.classList.contains('is-empty') && this.querySelector('.drawer__inner-empty')) || this.querySelector('#CartDrawer') || this.querySelector('.drawer__inner');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    this.unlockScroll();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const renderedDrawer = new DOMParser().parseFromString(parsedState.sections['cart-drawer'], 'text/html').querySelector('cart-drawer');
    if (renderedDrawer) this.classList.toggle('is-empty', renderedDrawer.classList.contains('is-empty'));
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

if (customElements && !customElements.get('cta-enable-subscription')) {
  class CTAEnableSubscription extends HTMLElement {
    connectedCallback() {
      this.handleActivation = (event) => {
        if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.enableSubscription();
      };
      this.addEventListener('click', this.handleActivation);
      this.addEventListener('keydown', this.handleActivation);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleActivation);
      this.removeEventListener('keydown', this.handleActivation);
    }

    async enableSubscription() {
      if (this.pending || !this.dataset.sellingPlanId) return;
      this.pending = true;
      this.setAttribute('aria-disabled', 'true');
      this.setAttribute('aria-busy', 'true');
      const root = window.Shopify?.routes?.root || '/';
      const releaseCart = window.NuoroCartGuard?.acquire();
      const checkout = document.querySelector('#CartDrawer-Checkout');
      const errorRegion = document.querySelector('#CartDrawer-CartErrors');
      if (checkout) checkout.disabled = true;
      if (errorRegion) errorRegion.textContent = '';
      try {
        const cartResponse = await fetch(`${root}cart.js`);
        if (!cartResponse.ok) throw new Error(window.cartStrings?.error || '');
        const cart = await cartResponse.json();
        const index = cart.items.findIndex((item) => item.key === this.dataset.key);
        if (index < 0) throw new Error(window.cartStrings?.error || '');
        const response = await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            line: index + 1,
            quantity: cart.items[index].quantity,
            selling_plan: Number(this.dataset.sellingPlanId),
          }),
        });
        const updatedCart = await response.json();
        if (!response.ok || updatedCart.errors) {
          throw new Error(updatedCart.description || updatedCart.message || window.cartStrings?.error || '');
        }
        await publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cta-enable-subscription',
          cartData: updatedCart,
          variantId: this.dataset.variantId,
        });
      } catch (error) {
        const currentErrors = document.querySelector('#CartDrawer-CartErrors');
        if (currentErrors) currentErrors.textContent = error.message || window.cartStrings?.error || '';
      } finally {
        releaseCart?.();
        this.pending = false;
        this.removeAttribute('aria-disabled');
        this.removeAttribute('aria-busy');
        const currentCheckout = document.querySelector('#CartDrawer-Checkout');
        if (currentCheckout && !window.NuoroCartGuard?.busy && !window.NuoroCartGuard?.error && !document.querySelector('cart-drawer')?.classList.contains('is-empty')) {
          currentCheckout.disabled = false;
        }
      }
    }
  }

  customElements.define('cta-enable-subscription', CTAEnableSubscription);
}
